import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { faker } from "@faker-js/faker";
import { db } from "../../firebase";
import { buildAffiliateTurnoverUpdate } from "../../lib/affiliateBonus";
import CrashHistoryTable from "./CrashHistoryTable";
import CrashUserHistoryTable from "./CrashUserHistoryTable";
import LiveBetsTable from "./LiveBetsTable";
import LiveCrashChart from "./LiveCrashChart";

function createLocalRound() {
  const now = Date.now();
  return {
    id: `local-${now}`,
    status: "waiting",
    crashAt: Number((1.15 + Math.random() * 8.85).toFixed(2)),
    startTime: now,
    waitUntil: now + 5500,
  };
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return Number(value) || 0;
}

function isBackendRoundHealthy(round) {
  if (!round) return false;
  const now = Date.now();
  const startTime = toMillis(round.startTime);
  const waitUntil = toMillis(round.waitUntil);

  if (round.status === "waiting") {
    if (waitUntil) {
      return waitUntil > now - 3000 && waitUntil < now + 90000;
    }
    return startTime > 0 && now - startTime < 12000;
  }

  if (round.status === "running") {
    return startTime > 0 && now - startTime < 30000;
  }

  return false;
}

function createBotPool(round) {
  return Array.from({ length: 18 }).map((_, index) => {
    const willCashout = Math.random() < 0.65;
    const maxTarget = Math.max(1.06, Number(round.crashAt || 2) - 0.05);

    return {
      userId: `bot-${index}`,
      username: faker.internet.userName(),
      avatar: faker.image.avatar(),
      amount: +faker.finance.amount(0.2, 250, 2),
      status: "active",
      willCashout,
      targetCashout: willCashout
        ? Number((Math.random() * (maxTarget - 1.05) + 1.05).toFixed(2))
        : null,
      cashedOutAt: null,
    };
  });
}

function formatMoney(value) {
  return `${Number(value || 0).toFixed(2)} USDT`;
}

export default function CrashGame({ user, userBalance, setUserBalance }) {
  const [backendRound, setBackendRound] = useState(null);
  const [backendBets, setBackendBets] = useState([]);
  const [backendMyBet, setBackendMyBet] = useState(null);
  const [backendHistory, setBackendHistory] = useState([]);

  const [localRound, setLocalRound] = useState(() => createLocalRound());
  const [localBets, setLocalBets] = useState([]);
  const [localMyBet, setLocalMyBet] = useState(null);
  const [localHistory, setLocalHistory] = useState([]);
  const [botLiveBets, setBotLiveBets] = useState([]);

  const [manualBetAmount, setManualBetAmount] = useState("10");
  const [manualAutoCashout, setManualAutoCashout] = useState("2.00");
  const [autoBetAmount, setAutoBetAmount] = useState("10");
  const [autoCashoutAt, setAutoCashoutAt] = useState("2.00");
  const [autoBetEnabled, setAutoBetEnabled] = useState(false);

  const [multiplier, setMultiplier] = useState(1);
  const [linePoints, setLinePoints] = useState([{ t: 0, m: 1 }]);
  const [placing, setPlacing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [loading, setLoading] = useState(true);

  const lineIntervalRef = useRef(null);
  const localCycleRef = useRef(null);
  const runningAudioRef = useRef(null);
  const autoBetQueuedRef = useRef(false);
  const cashoutInFlightRef = useRef(null);

  useEffect(() => {
    const roundQuery = query(
      collection(db, "crashRounds"),
      orderBy("startTime", "desc"),
      limit(1),
    );
    return onSnapshot(roundQuery, (snapshot) => {
      if (snapshot.empty) {
        setBackendRound(null);
        return;
      }
      setBackendRound({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
    });
  }, []);

  useEffect(() => {
    if (!backendRound) return undefined;
    const betsQuery = query(
      collection(db, "crashBets"),
      where("roundId", "==", backendRound.id),
    );
    return onSnapshot(betsQuery, (snapshot) => {
      const nextBets = snapshot.docs.map((entry) => ({
        id: entry.id,
        ...entry.data(),
      }));
      setBackendBets(nextBets);
      setBackendMyBet(
        nextBets.find((entry) => entry.userId === user?.uid) || null,
      );
    });
  }, [backendRound, user?.uid]);

  useEffect(() => {
    const historyQuery = query(
      collection(db, "crashRounds"),
      where("status", "==", "crashed"),
      orderBy("startTime", "desc"),
      limit(20),
    );
    return onSnapshot(
      historyQuery,
      (snapshot) => {
        setBackendHistory(
          snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })),
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, []);

  const useLocalMode = !isBackendRoundHealthy(backendRound);
  const round = useLocalMode ? localRound : backendRound;
  const bets = useLocalMode ? localBets : backendBets;
  const myBet = useLocalMode ? localMyBet : backendMyBet;

  useEffect(() => {
    if (!useLocalMode) {
      window.clearTimeout(localCycleRef.current);
      return undefined;
    }

    function queueLocalCycle() {
      const waitingRound = createLocalRound();
      setLocalRound(waitingRound);
      setLocalBets([]);
      setLocalMyBet(null);
      setBotLiveBets(createBotPool(waitingRound));

      window.clearTimeout(localCycleRef.current);
      localCycleRef.current = window.setTimeout(() => {
        const startedAt = Date.now();
        setLocalRound((current) =>
          current
            ? {
                ...current,
                status: "running",
                startedAt,
                startTime: startedAt,
              }
            : current,
        );

        const duration = 4600 + Math.log10(Math.max(1.1, waitingRound.crashAt)) * 1900;

        localCycleRef.current = window.setTimeout(() => {
          const crashedAt = Date.now();
          setLocalBets((current) =>
            current.map((entry) =>
              entry.status === "active"
                ? { ...entry, status: "crashed", cashedOutAt: null }
                : entry,
            ),
          );
          setLocalMyBet((current) =>
            current?.status === "active"
              ? { ...current, status: "crashed", cashedOutAt: null }
              : current,
          );
          setBotLiveBets((current) =>
            current.map((entry) =>
              entry.status === "active"
                ? { ...entry, status: "crashed", cashedOutAt: null }
                : entry,
            ),
          );
          setLocalRound((current) =>
            current
              ? {
                  ...current,
                  status: "crashed",
                  endedAt: crashedAt,
                }
              : current,
          );
          setLocalHistory((current) =>
            [
              {
                id: waitingRound.id,
                roundNumber: current.length + 1,
                crashAt: waitingRound.crashAt,
                startTime: waitingRound.startTime,
                status: "crashed",
              },
              ...current,
            ].slice(0, 20),
          );
          localCycleRef.current = window.setTimeout(queueLocalCycle, 2200);
        }, duration);
      }, 5500);
    }

    queueLocalCycle();
    return () => window.clearTimeout(localCycleRef.current);
  }, [useLocalMode]);

  useEffect(() => {
    if (!round) {
      setMultiplier(1);
      setLinePoints([{ t: 0, m: 1 }]);
      return undefined;
    }

    if (round.status !== "running") {
      setMultiplier(round.status === "crashed" ? Number(round.crashAt || 1) : 1);
      setLinePoints([{ t: 0, m: 1 }]);
      return undefined;
    }

    setMultiplier(1);
    setLinePoints([{ t: 0, m: 1 }]);

    const crashAt = Number(round.crashAt || 2);
    const totalTime = 7 + Math.log10(Math.max(1.1, crashAt)) * 2;
    const speed = Math.log(Math.max(1.1, crashAt)) / totalTime;
    const startedAt = Date.now();

    window.clearInterval(lineIntervalRef.current);
    lineIntervalRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      let nextMultiplier = Math.exp(speed * elapsed);
      if (nextMultiplier >= crashAt) {
        nextMultiplier = crashAt;
      }

      setMultiplier(nextMultiplier);
      setLinePoints((current) => [...current, { t: elapsed, m: nextMultiplier }]);

      if (nextMultiplier >= crashAt) {
        window.clearInterval(lineIntervalRef.current);
      }
    }, 45);

    return () => window.clearInterval(lineIntervalRef.current);
  }, [round?.id, round?.status, round?.crashAt]);

  useEffect(() => {
    if (!round || round.status !== "running") return;

    setBotLiveBets((current) =>
      current.map((entry) => {
        if (
          entry.status === "active" &&
          entry.willCashout &&
          multiplier >= entry.targetCashout
        ) {
          return { ...entry, status: "cashed", cashedOutAt: entry.targetCashout };
        }

        if (
          entry.status === "active" &&
          round.crashAt &&
          multiplier >= round.crashAt
        ) {
          return { ...entry, status: "crashed", cashedOutAt: null };
        }

        return entry;
      }),
    );
  }, [multiplier, round?.status, round?.crashAt]);

  useEffect(() => {
    if (round?.status === "running") {
      if (runningAudioRef.current) {
        runningAudioRef.current.pause();
        runningAudioRef.current.currentTime = 0;
      }
      runningAudioRef.current = new window.Audio("/sounds/crash-run.mp3");
      runningAudioRef.current.volume = 0.55;
      runningAudioRef.current.loop = true;
      runningAudioRef.current.play().catch(() => {});
    } else if (runningAudioRef.current) {
      runningAudioRef.current.pause();
      runningAudioRef.current.currentTime = 0;
      runningAudioRef.current = null;
    }

    return () => {
      if (runningAudioRef.current) {
        runningAudioRef.current.pause();
        runningAudioRef.current.currentTime = 0;
        runningAudioRef.current = null;
      }
    };
  }, [round?.status]);

  useEffect(() => {
    if (!round) return;

    if (
      round.status === "waiting" &&
      autoBetEnabled &&
      !myBet &&
      !autoBetQueuedRef.current
    ) {
      autoBetQueuedRef.current = true;
      placeBet(true, true);
    }

    if (round.status !== "waiting") {
      autoBetQueuedRef.current = false;
    }
  }, [autoBetEnabled, myBet, round?.id, round?.status]);

  useEffect(() => {
    if (!myBet || myBet.status !== "active" || round?.status !== "running") return;
    const target = Number(myBet.autoCashoutAt || autoCashoutAt || 0);
    if (target > 1 && multiplier >= target) {
      cashOut();
    }
  }, [autoCashoutAt, multiplier, myBet, round?.status]);

  useEffect(() => {
    if (!myBet || myBet.status !== "active" || round?.status !== "running") {
      cashoutInFlightRef.current = null;
    }
  }, [myBet?.id, myBet?.status, round?.id, round?.status]);

  async function updateUserBalance(delta) {
    const userRef = doc(db, "users", user.uid);
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(userRef);
      if (!snapshot.exists()) throw new Error("User not found");
      const currentBalance = Number(snapshot.data().balance || 0);
      transaction.update(userRef, {
        balance: +(currentBalance + delta).toFixed(2),
      });
    });
  }

  async function deductUserBalance(amount) {
    const userRef = doc(db, "users", user.uid);
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(userRef);
      if (!snapshot.exists()) throw new Error("User not found");
      const userData = snapshot.data() || {};
      const currentBalance = Number(userData.balance || 0);
      if (currentBalance < amount) throw new Error("Insufficient balance");
      const currentTurnover = Number(userData.turnoverCompleted || 0);
      const affiliateUpdate = buildAffiliateTurnoverUpdate(userData, amount);
      transaction.update(userRef, {
        balance: +(currentBalance - amount).toFixed(2),
        turnoverCompleted: +(currentTurnover + amount).toFixed(2),
        ...affiliateUpdate.fields,
      });
    });
  }

  function playSound(url) {
    const audio = new window.Audio(url);
    audio.volume = 0.55;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  const placeBet = async (silent = false, useAutoPanel = false) => {
    if (!silent) playSound("/sounds/xo-click.mp3");
    if (!user || !round || myBet || placing) return;

    const amount = Number(useAutoPanel ? autoBetAmount : manualBetAmount);
    const autoTarget = Number(useAutoPanel ? autoCashoutAt : manualAutoCashout);

    if (amount < 0.1) {
      alert("Minimum bet is 0.1");
      return;
    }
    if (amount <= 0 || Number(userBalance || 0) < amount) {
      alert("Insufficient balance");
      return;
    }
    if (round.status !== "waiting") return;

    setPlacing(true);
    try {
      if (setUserBalance) {
        setUserBalance((current) => Number(current || 0) - amount);
      }
      await deductUserBalance(amount);

      if (useLocalMode) {
        const nextBet = {
          id: `local-bet-${Date.now()}`,
          roundId: round.id,
          userId: user.uid,
          username: user.displayName || user.email || "Player",
          avatar: user.photoURL || "",
          amount,
          status: "active",
          cashedOutAt: null,
          autoCashoutAt: autoTarget > 1 ? autoTarget : null,
          createdAt: new Date(),
        };
        setLocalMyBet(nextBet);
        setLocalBets((current) => [...current, nextBet]);
      } else {
        await addDoc(collection(db, "crashBets"), {
          roundId: round.id,
          userId: user.uid,
          username: user.displayName,
          avatar: user.photoURL,
          amount,
          status: "active",
          cashedOutAt: null,
          autoCashoutAt: autoTarget > 1 ? autoTarget : null,
          createdAt: serverTimestamp(),
        });
      }
    } catch {
      if (setUserBalance) {
        setUserBalance((current) => Number(current || 0) + amount);
      }
      if (!silent) alert("Failed to place bet");
    }
    setPlacing(false);
  };

  const cancelBet = async () => {
    if (cancelling || !myBet || !round || round.status !== "waiting") return;
    setCancelling(true);
    try {
      await updateUserBalance(Number(myBet.amount || 0));

      if (useLocalMode) {
        setLocalBets((current) => current.filter((entry) => entry.id !== myBet.id));
        setLocalMyBet(null);
      } else {
        await updateDoc(doc(db, "crashBets", myBet.id), { status: "cancelled" });
      }

      playSound("/sounds/xo-click.mp3");
    } catch {
      alert("Failed to cancel bet and refund");
    } finally {
      setCancelling(false);
    }
  };

  const cashOut = async () => {
    if (!myBet || myBet.status !== "active" || !round || round.status !== "running") return;
    const cashoutKey = `${round.id}:${myBet.id}`;
    if (cashoutInFlightRef.current === cashoutKey) return;

    cashoutInFlightRef.current = cashoutKey;
    const cashedOutAt = Number(multiplier.toFixed(2));
    const payout = Number((myBet.amount * cashedOutAt).toFixed(2));
    try {
      await updateUserBalance(payout);
      if (useLocalMode) {
        setLocalBets((current) =>
          current.map((entry) =>
            entry.id === myBet.id
              ? { ...entry, status: "cashed", cashedOutAt }
              : entry,
          ),
        );
        setLocalMyBet((current) =>
          current ? { ...current, status: "cashed", cashedOutAt } : current,
        );
      } else {
        await updateDoc(doc(db, "crashBets", myBet.id), {
          cashedOutAt,
          status: "cashed",
        });
      }
      playSound("/sounds/cash-add.mp3");
    } catch {
      cashoutInFlightRef.current = null;
      alert("Failed to cash out");
    }
  };

  const canCancel = !!myBet && round?.status === "waiting" && myBet.status === "active";
  const canCashOut = !!myBet && myBet.status === "active" && round?.status === "running";
  const cashOutLabel = canCashOut
    ? `Cash Out ${(Number(myBet.amount || 0) * multiplier).toFixed(2)} USDT`
    : "Cash Out";

  const allBets = [...bets, ...botLiveBets];
  const cashouts = bets
    .filter((entry) => entry.status === "cashed")
    .map((entry) => ({
      name: entry.username || entry.userId?.slice(0, 5) || "User",
      cashout: entry.cashedOutAt,
    }));

  const volume = useMemo(
    () => allBets.reduce((total, entry) => total + Number(entry.amount || 0), 0),
    [allBets],
  );
  const activePlayers = useMemo(
    () => allBets.filter((entry) => entry.status === "active").length,
    [allBets],
  );
  const rounds = useMemo(() => {
    const merged = [...localHistory, ...backendHistory];
    return merged
      .sort((left, right) => toMillis(right.startTime) - toMillis(left.startTime))
      .slice(0, 20);
  }, [backendHistory, localHistory]);
  const recentMultipliers = useMemo(
    () => rounds.slice(0, 14).map((entry) => Number(entry.crashAt || 0)),
    [rounds],
  );

  return (
    <div className="min-h-screen bg-[#171b22] px-2 py-4 text-white md:mt-16 md:px-4">
      <div className="mx-auto max-w-[1500px]">
        <div className="mb-4 flex flex-wrap gap-2 overflow-x-auto rounded-[24px] bg-[#1f242d] p-3 shadow-[0_20px_50px_rgba(0,0,0,0.24)]">
          {recentMultipliers.map((value, index) => (
            <span
              key={`${value}-${index}`}
              className={`rounded-[14px] px-3 py-2 text-xs font-bold ${
                value < 2
                  ? "bg-[#3a1f26] text-[#ff8f9d]"
                  : value < 5
                    ? "bg-[#332715] text-[#ffb45c]"
                    : "bg-[#183223] text-[#54df8a]"
              }`}
            >
              {value.toFixed(2)}x
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[280px_minmax(0,1fr)_280px] 2xl:grid-cols-[300px_minmax(0,1fr)_320px]">
          <aside className="order-2 rounded-[28px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)] xl:order-1">
            <div className="grid grid-cols-2 gap-2 rounded-[18px] bg-[#171b22] p-1 text-sm font-semibold">
              <span className="rounded-[14px] bg-[#242b36] px-3 py-2 text-center text-white">
                Manual
              </span>
              <span className="rounded-[14px] px-3 py-2 text-center text-slate-500">
                Auto
              </span>
            </div>

            <BetCard
              title="Manual Bet"
              amount={manualBetAmount}
              setAmount={setManualBetAmount}
              autoCashout={manualAutoCashout}
              setAutoCashout={setManualAutoCashout}
              round={round}
              myBet={myBet}
              placing={placing}
              onBet={() => placeBet(false, false)}
              quickValues={[1, 10, 100, 1000]}
            />

            <BetCard
              title="Auto Bet"
              amount={autoBetAmount}
              setAmount={setAutoBetAmount}
              autoCashout={autoCashoutAt}
              setAutoCashout={setAutoCashoutAt}
              round={round}
              myBet={myBet}
              placing={placing}
              onBet={() => placeBet(false, true)}
              quickValues={[1, 10, 100, 1000]}
              autoBetEnabled={autoBetEnabled}
              setAutoBetEnabled={setAutoBetEnabled}
            />

            <div className="mt-4 space-y-2">
              {canCancel ? (
                <button
                  onClick={cancelBet}
                  disabled={cancelling}
                  className="w-full rounded-[18px] bg-[#41311f] px-4 py-3 text-sm font-bold text-[#ffc166]"
                >
                  {cancelling ? "Cancelling..." : "Cancel Bet"}
                </button>
              ) : null}
              <button
                onClick={cashOut}
                disabled={!canCashOut}
                className="w-full rounded-[18px] bg-[#31c96a] px-4 py-3 text-sm font-black text-[#07110d] disabled:bg-[#2b313c] disabled:text-slate-500"
              >
                {cashOutLabel}
              </button>
            </div>
          </aside>

          <section className="order-1 rounded-[28px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)] xl:order-2">
            <div className="rounded-[22px] bg-[#181d25] p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
                    Crash
                  </div>
                  <div className="mt-2 text-3xl font-black text-white">
                    {round?.status === "running"
                      ? `${multiplier.toFixed(2)}x`
                      : round?.status === "waiting"
                        ? "Betting Open"
                        : `${Number(round?.crashAt || 1).toFixed(2)}x`}
                  </div>
                </div>
                <div className="rounded-full bg-[#232a35] px-4 py-2 text-xs font-bold text-emerald-300">
                  {useLocalMode ? "Local live mode" : "Backend live mode"}
                </div>
              </div>

              <div className="mt-4 rounded-[24px] bg-[#14181f] p-4">
                <LiveCrashChart
                  round={round}
                  multiplier={multiplier}
                  crashedAt={round?.status === "crashed" ? round.crashAt : null}
                  linePoints={linePoints}
                  cashouts={cashouts}
                />
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <HudStat
                  label="Balance"
                  value={formatMoney(userBalance)}
                  tone="text-white"
                />
                <HudStat
                  label="Players"
                  value={String(activePlayers)}
                  tone="text-emerald-300"
                />
                <HudStat
                  label="Volume"
                  value={formatMoney(volume)}
                  tone="text-amber-300"
                />
              </div>
            </div>
          </section>

          <section className="order-3 space-y-4">
            <div className="rounded-[28px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
              <div className="text-sm font-bold text-white">All Bets</div>
              <LiveBetsTable
                bets={allBets}
                crashedAt={round?.status === "crashed" ? round.crashAt : null}
              />
            </div>
            <div className="rounded-[28px] bg-[#1f242d] p-4 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
              <div className="text-sm font-bold text-white">Recent Rounds</div>
              <CrashHistoryTable rounds={rounds} loading={loading} />
            </div>
          </section>
        </div>

        <CrashUserHistoryTable user={user} />
      </div>
    </div>
  );
}

function HudStat({ label, value, tone = "text-white" }) {
  return (
    <div className="rounded-[18px] bg-[#14181f] px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
        {label}
      </div>
      <div className={`mt-2 text-sm font-black ${tone}`}>{value}</div>
    </div>
  );
}

function BetCard({
  title,
  amount,
  setAmount,
  autoCashout,
  setAutoCashout,
  round,
  myBet,
  placing,
  onBet,
  quickValues,
  autoBetEnabled,
  setAutoBetEnabled,
}) {
  return (
    <div className="mt-4 rounded-[22px] bg-[#171d26] p-4">
      <div className="mb-3 text-sm font-bold text-white">{title}</div>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.24em] text-slate-500">
            Amount
          </label>
          <input
            type="number"
            value={amount}
            min={0.1}
            step={0.1}
            onChange={(event) => setAmount(event.target.value)}
            disabled={!!myBet || !round || round.status !== "waiting"}
            className="mt-2 w-full rounded-[16px] bg-[#202631] px-4 py-3 text-white outline-none"
          />
          <div className="mt-2 grid grid-cols-4 gap-2">
            {quickValues.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setAmount(String(value))}
                className="rounded-[14px] bg-[#202631] px-2 py-2 text-xs font-semibold text-slate-300 transition hover:bg-[#29303b]"
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.24em] text-slate-500">
            Auto Cashout
          </label>
          <input
            type="number"
            min={1.01}
            step={0.01}
            value={autoCashout}
            onChange={(event) => setAutoCashout(event.target.value)}
            className="mt-2 w-full rounded-[16px] bg-[#202631] px-4 py-3 text-white outline-none"
          />
        </div>

        {typeof autoBetEnabled === "boolean" ? (
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={autoBetEnabled}
              onChange={(event) => setAutoBetEnabled(event.target.checked)}
            />
            Enable auto bet
          </label>
        ) : null}

        <button
          type="button"
          onClick={onBet}
          disabled={!!myBet || !round || round.status !== "waiting" || placing || !amount}
          className="w-full rounded-[16px] bg-[#31c96a] px-4 py-3 text-sm font-black text-[#07110d] disabled:bg-[#2b313c] disabled:text-slate-500"
        >
          {placing ? "Placing..." : "Bet"}
        </button>
      </div>
    </div>
  );
}
